import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, IconButton, Typography } from '@mui/material';

import { useFilterContext } from 'src/components/app/context/FilterContext';
import useOnCurrentResultFilterChange from 'src/components/followUp/hooks/useOnCurrentResultFilterChange';

import { styles } from './TablePagination.styles';

interface TablePaginationProps {
  count: number;
  previous: string;
  next: string;
  offset: number;
  setOffset: (setValueFunc: ((value: number) => number) | number) => void;
  pageSize: number;
}

const TablePagination: FC<TablePaginationProps> = ({
  count,
  previous,
  next,
  offset,
  setOffset,
  pageSize,
}) => {
  const { t } = useTranslation('followUp');

  const { filterGroups } = useFilterContext();
  useOnCurrentResultFilterChange(filterGroups, () => setOffset(0));

  return (
    <Box sx={styles.tablePagination} aria-label="Pagination">
      <Typography
        sx={(theme) => ({ fontWeight: 600, color: theme.colors.neutral[700] })}
      >
        {t('paginationString', {
          firstId: offset + 1,
          lastId: Math.min(offset + 10, count),
          totalTickets: count,
        })}
      </Typography>
      <IconButton
        size="small"
        sx={(theme) => ({
          color: theme.colors.neutral[800],
        })}
        disabled={!previous}
        onClick={() => setOffset((prev) => prev - pageSize)}
        aria-label="previous-page"
      >
        <ArrowBackIosIcon fontSize="inherit" />
      </IconButton>

      <IconButton
        size="small"
        sx={(theme) => ({
          color: theme.colors.neutral[800],
        })}
        disabled={!next}
        onClick={() => setOffset((prev) => prev + pageSize)}
        aria-label="next-page"
      >
        <ArrowForwardIosIcon fontSize="inherit" />
      </IconButton>
    </Box>
  );
};

export default TablePagination;
